/**
 * Shared block-render helpers — ported verbatim from the legacy monolith
 * (salesBanner, __cardHtml/__cardLayout, __cdParts/__cdCells, __pfFmt).
 */
import { AR, esc, btnHTML, theme, type ThemeSpec, escAttr } from "./core";

export interface CardItem {
  img?: string;
  name?: string;
  desc?: string;
  sqft?: string;
  beds?: string;
  baths?: string;
  link?: string;
  url?: string;
}

interface CardLayout {
  cols: number;
  w: number;
  h: number;
}

/** card layout presets — layout pinned to the original 1-across (2026-07-22, per Jack) */
export function cardLayout(p?: string): CardLayout {
  const M: Record<string, [number, number, number]> = {
    "1x180": [1, 270, 180], "1x166": [1, 270, 166], "1x144": [1, 270, 144],
    "2x175": [2, 269, 175], "2x145": [2, 269, 145],
    "3x160": [3, 179, 160], "3x125": [3, 179, 125],
  };
  const a = M[p || "1x180"] || M["1x180"];
  return { cols: a[0], w: a[1], h: a[2] };
}

export interface CardBlockData {
  items?: CardItem[];
  __title?: string;
  __theme?: string;
}

/** shared Featured Community/Floorplan renderer — legacy __cardHtml */
export function cardHtml(d: CardBlockData, defTitle: string): string {
  const items = Array.isArray(d.items) ? d.items : [];
  const L = cardLayout("1x180"),
    TH: ThemeSpec = theme(d.__theme),
    title = esc(d.__title || defTitle);
  const band =
    '<tr><td style="background:' + TH.band + ";padding:14px 40px" + TH.border +
    '"><table role="presentation" width="100%" cellpadding="0" cellspacing="0"><tr><td style="font-family:' + AR +
    ";font-size:16px;font-weight:800;color:" + TH.bandTx + '">' + title +
    '</td><td align="right" style="font-family:' + AR + ';font-size:12px"><a href="#" style="color:' + TH.view +
    ';text-decoration:underline">View All</a></td></tr></table></td></tr>';
  const imgCell = (it: CardItem, w: number, h: number) =>
    it.img
      ? '<img src="' + escAttr(it.img) + '" width="' + w + '" height="' + h + '" alt="' + escAttr(it.name || "") +
        '" style="display:block;width:' + w + "px;height:" + h + 'px;max-width:100%;object-fit:cover;border:0;border-radius:4px">'
      : '<div style="width:' + w + "px;height:" + h + 'px;max-width:100%;background:#d5d7da;border-radius:4px"></div>';
  const btn = (it: CardItem) =>
    '<div style="padding-top:12px"><a href="' + escAttr(it.url || "#") + '" style="display:inline-block;background:' + TH.btn +
    ";color:#ffffff;text-decoration:none;font-family:" + AR +
    ';font-size:13px;font-weight:bold;padding:11px 26px;border-radius:24px">' + esc(it.link || "View Floorplan") + "</a></div>";
  const specs = (it: CardItem) => [it.sqft, it.beds, it.baths].filter(Boolean).map((x) => esc(x)).join("<br>");
  let body = "";
  if (L.cols === 1) {
    body = items
      .map(
        (it, i) =>
          '<tr><td style="padding:16px 40px' + (i ? ";border-top:1px solid #e7e3d9" : "") +
          '"><table role="presentation" width="100%" cellpadding="0" cellspacing="0"><tr><td width="' + L.w +
          '" valign="middle" style="font-size:0">' + imgCell(it, L.w, L.h) +
          '</td><td valign="middle" style="padding-left:18px;font-family:' + AR +
          '"><div style="font-size:18px;font-weight:800;color:#1B365D">' + esc(it.name || "") +
          '</div><div style="font-size:12.5px;color:#8a8578;padding-top:4px">' + esc(it.desc || "") +
          '</div><div style="font-size:12.5px;color:#303030;line-height:1.75;padding-top:6px">' + specs(it) + "</div>" +
          btn(it) + "</td></tr></table></td></tr>",
      )
      .join("");
  } else {
    const cw = (100 / L.cols).toFixed(4) + "%";
    const grp: CardItem[][] = [];
    for (let i = 0; i < items.length; i += L.cols) grp.push(items.slice(i, i + L.cols));
    body = grp
      .map((row) => {
        const cells = row.map(
          (it) =>
            '<td valign="top" width="' + cw + '" style="padding:12px 10px;font-family:' + AR +
            ';vertical-align:top"><div style="font-size:0">' + imgCell(it, L.w, L.h) +
            '</div><div style="font-size:15px;font-weight:800;color:#1B365D;padding-top:8px">' + esc(it.name || "") +
            '</div><div style="font-size:11.5px;color:#8a8578;padding-top:2px">' + esc(it.desc || "") +
            '</div><div style="font-size:11.5px;color:#303030;line-height:1.6;padding-top:5px">' + specs(it) + "</div>" +
            btn(it) + "</td>",
        );
        while (cells.length < L.cols) cells.push('<td width="' + cw + '"></td>');
        return '<tr><td style="padding:6px 30px"><table role="presentation" width="100%" cellpadding="0" cellspacing="0"><tr>' + cells.join("") + "</tr></table></td></tr>";
      })
      .join("");
  }
  return '<tr><td style="padding:0"><table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse">' + band + body + "</table></td></tr>";
}

export interface SalesBannerData {
  eyebrow?: string;
  headline?: string;
  sub?: string;
  label?: string;
  url?: string;
  bg?: string;
}

/** shared Offer & finance banner — legacy salesBanner */
export function salesBanner(d: SalesBannerData): string {
  const navy = d.bg === "navy";
  return (
    '<tr><td align="center" style="padding:28px 40px;background:' + (navy ? "#1B2A58" : "#F6F2EA") + '">' +
    (d.eyebrow
      ? '<div style="font-family:' + AR + ';font-size:12px;font-weight:bold;letter-spacing:.16em;text-transform:uppercase;color:' +
        (navy ? "#C8A664" : "#a9843f") + '">' + esc(d.eyebrow) + "</div>"
      : "") +
    '<div style="font-family:' + AR + ';font-size:26px;font-weight:800;line-height:1.15;padding-top:6px;color:' +
    (navy ? "#ffffff" : "#1B365D") + '">' + esc(d.headline) + "</div>" +
    (d.sub
      ? '<div style="font-family:' + AR + ';font-size:14px;line-height:20px;padding-top:8px;color:' +
        (navy ? "#c9d4e6" : "#303030") + '">' + esc(d.sub) + "</div>"
      : "") +
    (d.label ? '<div style="padding-top:16px">' + btnHTML(d.label!, d.url || "", navy ? "outline" : "filled") + "</div>" : "") +
    "</td></tr>"
  );
}

export interface CountdownParts { d: number; h: number; m: number; s: number; }

/** legacy __cdParts — no deadline = the fixed demo remainder so previews are deterministic */
export function cdParts(dl?: string): CountdownParts {
  const t = dl ? Date.parse(String(dl).replace(" ", "T")) : 0;
  const diff = t && !isNaN(t) ? Math.max(0, t - Date.now()) : (3 * 86400 + 14 * 3600 + 22 * 60 + 9) * 1000;
  const s = Math.floor(diff / 1000);
  return { d: Math.floor(s / 86400), h: Math.floor((s % 86400) / 3600), m: Math.floor((s % 3600) / 60), s: s % 60 };
}

/** legacy __cdCells — static countdown tile row (the animated version is a generated GIF) */
export function cdCells(r: CountdownParts): string {
  const p2 = (n: number) => (n < 10 ? "0" : "") + n;
  const cell = (v: number, l: string) =>
    '<td align="center" style="padding:0 5px"><table role="presentation" cellpadding="0" cellspacing="0"><tr><td align="center" bgcolor="#243459" style="background:#243459;border-radius:8px;padding:8px 12px;font-family:' +
    AR + ';font-size:26px;font-weight:900;color:#ffffff">' + p2(v) +
    '</td></tr><tr><td align="center" style="font-family:' + AR +
    ';font-size:9px;letter-spacing:.06em;text-transform:uppercase;color:#aab6d0;padding-top:5px">' + l + "</td></tr></table></td>";
  return '<table role="presentation" cellpadding="0" cellspacing="0" align="center"><tr>' + cell(r.d, "Days") + cell(r.h, "Hrs") + cell(r.m, "Min") + cell(r.s, "Sec") + "</tr></table>";
}

/** legacy __pfFmt — price/rate formatting for the Price Flip module */
export function pfFmt(v: unknown, u?: string): string {
  const n = parseFloat(String(v).replace(/[^0-9.-]/g, ""));
  if (isNaN(n)) return String(v == null ? "" : v);
  if (u === "$") return "$" + Math.round(n).toLocaleString();
  if (u === "%") return n.toFixed(2) + "%";
  return Math.abs(n % 1) > 0 ? n.toFixed(2) : String(Math.round(n));
}
